//? React
import { useState } from "react";

//? External
import { Spinner, Card, Dropdown } from "react-bootstrap";
import { FaListUl, FaPlus } from "react-icons/fa";

//* Sanitization
import { parseHtmlToText } from "../../../utils/questions/questionParseText";
import DOMPurify from "dompurify";

//* Data hooks + API
import { useQuestionLists } from "../../../hooks/questionLists/useQuestionLists";
import { questionListsAPI } from "../../../hooks/questionLists/questionListsAPI";
import { useQuestions } from "../../../hooks/questions/useQuestions";

//? Types
import { QuestionDetails } from "../../../hooks/questions/types/types";

interface ExamListPickerProps {
  token: string;
  existingQuestionIds: string[];
  onAddQuestion: (question: QuestionDetails) => void;
  onError: (message: string) => void;
}

/**
 * Lets the user pick one of their saved lists and inject every question of that
 * list directly into the exam (dedup against questions already present).
 * Uses the exact same fetch+sanitize path as ExamCartSection.handleAddSelected.
 */
export default function ExamListPicker({
  token,
  existingQuestionIds,
  onAddQuestion,
  onError,
}: ExamListPickerProps) {
  const { lists, loading } = useQuestionLists(token);
  const { getQuestionById } = useQuestions(token);

  const [adding, setAdding] = useState(false);

  const handlePickList = async (listId: string) => {
    setAdding(true);
    try {
      const detail = await questionListsAPI.getById(listId, token);
      const questionIds = detail.questions.map((q) => q.id);

      let ignored = 0;
      let added = 0;
      for (const questionId of questionIds) {
        if (existingQuestionIds.includes(questionId)) {
          ignored += 1;
          continue;
        }

        // Mesmo caminho do carrinho: detalhes completos + sanitização
        const details = await getQuestionById(questionId);
        if (details) {
          const cleaned: QuestionDetails = {
            ...details,
            statement: parseHtmlToText(
              details.statement ?? "",
              DOMPurify.sanitize
            ) as string,
          };
          onAddQuestion(cleaned);
          added += 1;
        }
      }

      if (added === 0 && ignored > 0) {
        onError(`Todas as ${ignored} questão(ões) da lista já estavam na prova.`);
      } else if (ignored > 0) {
        onError(`${ignored} questão(ões) já estavam na prova e foram ignoradas.`);
      }
    } catch (error: any) {
      onError(error?.message || "Erro ao adicionar a lista à prova.");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-2">
        <Spinner animation="border" variant="primary" size="sm" />
      </div>
    );
  }

  if (lists.length === 0) {
    return null;
  }

  return (
    <Card className="my-2">
      <Card.Body className="d-flex align-items-center gap-2">
        <strong className="me-2">
          <FaListUl className="me-2" />
          Adicionar lista à prova
        </strong>
        <Dropdown>
          <Dropdown.Toggle
            id="dropdown-exam-list-picker"
            variant="outline-primary"
            size="sm"
            disabled={adding}
          >
            {adding ? "Adicionando..." : "Escolher lista"}
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ maxHeight: "240px", overflowY: "auto" }}>
            {lists.map((list) => (
              <Dropdown.Item
                key={list.id}
                onClick={() => handlePickList(list.id)}
              >
                <FaPlus className="me-2" />
                {list.name}{" "}
                <small className="text-muted">({list.itemsCount})</small>
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Card.Body>
    </Card>
  );
}
